// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the tap machine. Pure: it takes the state and what the finger did, and returns what
// the UI should look like next. No DOM, no timers, no network, which is what lets the whole
// interaction be tested without a browser.
//
// A TURN IS TWO DECISIONS AND THE UI NEVER MERGES THEM. Lift an offer, then place it. The two-step
// is what keeps a mis-tap from spending your claim: nothing is committed until CONFIRM, and a drag
// across the board only ever MOVES the ghost — releasing never commits. That rule is worth more on a
// phone than any amount of hit-target tuning, because the failure it prevents is unrecoverable.

import { doubletOf, type Doublet } from './deck';
import {
  ROTS,
  encodeMove,
  hasPlacement,
  isBurnTurn,
  legalAt,
  legalPlacements,
  openPicks,
  seatToPlay,
  type State,
} from './rules';

export type Phase = 'idle' | 'lifted' | 'placing' | 'burning';

export interface TurnUi {
  phase: Phase;
  /** The doublet number under consideration. */
  pick: number | null;
  /** Where the ghost is anchored, or null before the first tap on the board. */
  cell: number | null;
  rot: number;
}

export const freshTurn = (): TurnUi => ({ phase: 'idle', pick: null, cell: null, rot: 0 });

export interface Offer {
  n: number;
  doub: Doublet;
  open: boolean;
  claimedBy: number;
  /** Placeable somewhere on my demesne — false means claiming it spoils two cells. */
  placeable: boolean;
  /** The slot claiming it would buy me next round, as a range, or null in the final round. */
  slot: { lo: number; hi: number; of: number } | null;
}

/** The row as the player sees it: always ascending, always the whole row including what is gone. */
export function offers(s: State, seat: number, slotRange: (s: State, n: number) => Offer['slot']): Offer[] {
  const open = new Set(openPicks(s).map((i) => s.reveal[i]));
  return s.reveal.map((n, i) => {
    const doub = doubletOf(n) as Doublet;
    const isOpen = open.has(n);
    return {
      n,
      doub,
      open: isOpen,
      claimedBy: s.claimedBy[i],
      placeable: seat >= 0 && isOpen ? hasPlacement(s.boards[seat], s.mode, s.side, doub) : false,
      slot: isOpen ? slotRange(s, n) : null,
    };
  });
}

/** Anchors that are legal for the lifted doublet at the CURRENT rotation. */
export function legalAnchors(s: State, seat: number, ui: TurnUi): Set<number> {
  const out = new Set<number>();
  if (ui.pick === null || seat < 0) return out;
  const doub = doubletOf(ui.pick);
  if (!doub) return out;
  const n = s.side * s.side;
  for (let cell = 0; cell < n; cell++) if (legalAt(s.boards[seat], s.mode, s.side, doub, cell, ui.rot)) out.add(cell);
  return out;
}

/** Anchors where SOME rotation works — drawn differently, so "rotate me" is visible without trying. */
export function otherRotAnchors(s: State, seat: number, ui: TurnUi): Set<number> {
  const out = new Set<number>();
  if (ui.pick === null || seat < 0) return out;
  const doub = doubletOf(ui.pick);
  if (!doub) return out;
  for (const sp of legalPlacements(s.boards[seat], s.mode, s.side, doub)) {
    const cell = (sp / ROTS) | 0;
    if (sp % ROTS !== ui.rot) out.add(cell);
  }
  return out;
}

/** Lift an offer. Returns false if it is not this player's to lift. */
export function lift(s: State, seat: number, ui: TurnUi, n: number): boolean {
  if (seat < 0 || seatToPlay(s) !== seat) return false;
  if (!openPicks(s).some((i) => s.reveal[i] === n)) return false;
  ui.pick = n;
  ui.cell = null;
  ui.rot = 0;
  ui.phase = isBurnTurn(s) ? 'burning' : 'lifted';
  return true;
}

/** Move to the placing step, or report that this doublet has nowhere to go. */
export function confirmClaim(s: State, seat: number, ui: TurnUi): 'placing' | 'spoil' | 'no' {
  if (ui.phase !== 'lifted' || ui.pick === null) return 'no';
  const doub = doubletOf(ui.pick);
  if (!doub) return 'no';
  if (!hasPlacement(s.boards[seat], s.mode, s.side, doub)) return 'spoil';
  ui.phase = 'placing';
  // Start the ghost on the best-looking legal anchor rather than nowhere, so the first thing the
  // player sees is a real placement they can accept, nudge or reject.
  const spots = legalPlacements(s.boards[seat], s.mode, s.side, doub);
  if (spots.length > 0) {
    ui.cell = (spots[0] / ROTS) | 0;
    ui.rot = spots[0] % ROTS;
  }
  return 'placing';
}

/** Point the ghost at a cell. Never commits — that is CONFIRM's job, and only CONFIRM's. */
export function aim(s: State, seat: number, ui: TurnUi, cell: number): boolean {
  if (ui.phase !== 'placing' || ui.pick === null) return false;
  const doub = doubletOf(ui.pick);
  if (!doub) return false;
  if (ui.cell === cell) {
    // Tapping the anchor again cycles rotation, which is the fastest thing a thumb can do and means
    // the ROTATE button is a convenience rather than the only way round.
    return rotate(s, seat, ui);
  }
  ui.cell = cell;
  if (!legalAt(s.boards[seat], s.mode, s.side, doub, cell, ui.rot)) {
    // Snap to a rotation that works here, if one does. Nothing is more annoying than a legal square
    // that reads as illegal because the ghost happens to be the wrong way round.
    for (let r = 0; r < ROTS; r++) {
      if (legalAt(s.boards[seat], s.mode, s.side, doub, cell, (ui.rot + r) % ROTS)) {
        ui.rot = (ui.rot + r) % ROTS;
        break;
      }
    }
  }
  return true;
}

export function rotate(s: State, seat: number, ui: TurnUi): boolean {
  if (ui.pick === null) return false;
  const doub = doubletOf(ui.pick);
  if (!doub) return false;
  if (ui.cell === null) {
    ui.rot = (ui.rot + 1) % ROTS;
    return true;
  }
  // Prefer the next rotation that is actually legal here; fall back to plain next so the player can
  // always see all four, including the ones that do not fit.
  for (let r = 1; r <= ROTS; r++) {
    const cand = (ui.rot + r) % ROTS;
    if (legalAt(s.boards[seat], s.mode, s.side, doub, ui.cell, cand)) {
      ui.rot = cand;
      return true;
    }
  }
  ui.rot = (ui.rot + 1) % ROTS;
  return true;
}

/** Is the ghost currently on a legal placement? */
export function canPlace(s: State, seat: number, ui: TurnUi): boolean {
  if (ui.phase !== 'placing' || ui.pick === null || ui.cell === null) return false;
  const doub = doubletOf(ui.pick);
  return !!doub && legalAt(s.boards[seat], s.mode, s.side, doub, ui.cell, ui.rot);
}

/** The move this UI state represents, or -1 if it is not yet a move. */
export function moveFor(s: State, seat: number, ui: TurnUi): number {
  if (ui.pick === null) return -1;
  if (ui.phase === 'burning') return encodeMove({ round: s.round, slot: s.slot, pick: ui.pick });
  const doub = doubletOf(ui.pick);
  if (!doub) return -1;
  if (!hasPlacement(s.boards[seat], s.mode, s.side, doub)) {
    return encodeMove({ round: s.round, slot: s.slot, pick: ui.pick, discard: true });
  }
  if (!canPlace(s, seat, ui)) return -1;
  return encodeMove({ round: s.round, slot: s.slot, pick: ui.pick, cell: ui.cell as number, rot: ui.rot });
}

/** The one-line instruction under the board. It must always say what to do next. */
export function promptFor(s: State, seat: number, ui: TurnUi, mode: { burn: boolean }): string {
  if (seatToPlay(s) !== seat) return '';
  if (isBurnTurn(s)) {
    return ui.pick === null
      ? 'You claim last this round — strike one doublet out of the row first.'
      : `Burn #${ui.pick}? It leaves the match for good.`;
  }
  if (ui.phase === 'idle') return mode.burn ? 'Claim a doublet from the row.' : 'Claim a doublet — the lowest number picks first next round.';
  if (ui.phase === 'lifted') return `#${ui.pick} — claim it, or pick another.`;
  if (ui.cell === null) return 'Tap a square to lay it down.';
  return canPlace(s, seat, ui) ? 'Place it, or tap elsewhere to move it.' : 'No match here — rotate it, or tap another square.';
}
